import { Fragment, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
  SortingState,
} from "@tanstack/react-table";

import { ILocation, LocationType } from "../../models/Location";

export default function LocationsTable({ locations, locationCounts, setLocation }: { locations: ILocation[], locationCounts: Record<string, number>, setLocation: any }) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const colHelper = createColumnHelper<ILocation>();

  const cols = [
    colHelper.display({
      id: "name",
      header: "Name",
      sortingFn: "auto",
      cell: (info) => (
        <span className="flex  " >
          <span className="ml-4 m-auto">{info.row.original.name}</span>
        </span>
      )
    }),
    colHelper.display({
      id: "type",
      header: "Type",
      sortingFn: "auto",
      cell: (info) => info.row.original.type === LocationType.Racking ? "Racking" : "Yard"
    }),
    colHelper.display({
      id: "capacity",
      header: "Capacity",
      sortingFn: "auto",
      cell: (info) => info.row.original.capacity ?? "-"
    }),
    colHelper.display({
      id: "vehicleCount",
      header: "Vehicles",
      sortingFn: "auto",
      cell: (info) => {
        const count = locationCounts[info.row.original.id] ?? 0;
        const capacity = info.row.original.capacity;

        if (capacity !== undefined && capacity !== null) {
          return `${count}/${capacity}`;
        }

        return count;
      }
    }),
  ];

  const table = useReactTable({
    data: locations,
    columns: cols,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <div className="mt-4 bg-white overflow-auto">
        <table className="min-w-full overflow-scroll divide-y border divide-gray-300">

          {/* Table headings - very similar to all the other tables */}
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
                {headerGroup.headers.map((header, i) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                  >
                    {header.isPlaceholder
                      ? null
                      : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>

                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          
          {/* Body */}
          <tbody>
          {table.getRowModel().rows.map((row, i) => {
              let rowCells = row.getVisibleCells();
              return (
                <Fragment key={i}>
                  <tr
                    key={row.id}
                    onClick={() => setLocation(row.original)}
                    className={"hover:bg-gray-100 hover:cursor-pointer"}
                  >
                    {rowCells.map((cell) => (
                      <td key={cell.id} className="px-3 py-3 border text-sm">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                    <></>
                    // <tr>
                    //   {/* 2nd row is a custom 1 cell row */}
                    //   <td colSpan={row.getVisibleCells().length}>
                    //     <LeadsTableSubComponent lead={row.original} />
                    //   </td>
                    // </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>

        </table>
      </div>
    </>
  );
}
